import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import './trouve-replica.css';
import './trouve-elevated.css';
import Login from './components/Login';
import TrouveReplica from './components/TrouveReplica';
import { getHttpURL, getWebSocketURL } from './config/api';

const STREAM_NOTICE_MESSAGES = new Set([
  'Browser has been closed due to inactivity. Please start a conversation.',
  'Browser is not running. Please start a conversation to start the browser.',
]);

const AGENT_MODES = new Set(['netsuite', 'meridian', 'greenshield']);
const AGENT_MODE_STORAGE_KEY = 'trouveAgentMode';

const readStoredAgentMode = () => {
  try {
    const stored = String(window.localStorage.getItem(AGENT_MODE_STORAGE_KEY) || '').toLowerCase();
    if (AGENT_MODES.has(stored)) return stored;
  } catch (_) {}
  return 'netsuite';
};

const storeAgentMode = (mode) => {
  try {
    if (AGENT_MODES.has(mode)) window.localStorage.setItem(AGENT_MODE_STORAGE_KEY, mode);
  } catch (_) {}
};

// Normalize a /netsuite/session snapshot (or netsuite_session_change ws payload)
// into the bits the UI shows. Returns null when nothing is known yet.
const normalizeNetsuiteSession = (snapshot) => {
  const instanceName = String(
    snapshot?.current_netsuite_instance_name || snapshot?.netsuite_instance_name || ''
  ).trim();
  const userName = String(snapshot?.user_name || '').trim();
  const role = String(snapshot?.current_role || '').trim();
  if (!instanceName && !userName && !role) return null;
  return { instanceName, userName, role };
};

const SESSION_NAVIGATION_GUARD_KEY = 'trouveSessionNavigationGuard';
const SESSION_LEAVE_WARNING =
  'You have an ongoing session. Leaving this page will disconnect it. Are you sure you want to go back?';

function App() {
  const [messages, setMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isOpeningLiveBrowser, setIsOpeningLiveBrowser] = useState(false);
  const [browserFrameSrc, setBrowserFrameSrc] = useState(null);
  const [browserStreamNotice, setBrowserStreamNotice] = useState(null);
  const [currentPageUrl, setCurrentPageUrl] = useState(null);
  const [containerStatus, setContainerStatus] = useState(null);
  const [querySections, setQuerySections] = useState([]);
  const [netsuiteSession, setNetsuiteSession] = useState(null);
  const [agentMode, setAgentMode] = useState(readStoredAgentMode);
  const [onboardingModalOpen, setOnboardingModalOpen] = useState(false);
  // Dev-only: set REACT_APP_BYPASS_AUTH=true in .env to skip the Google login
  // screen and render the app directly (handy for previewing the UI locally).
  const BYPASS_AUTH = process.env.REACT_APP_BYPASS_AUTH === 'true';
  const [user, setUser] = useState(BYPASS_AUTH ? { name: 'Dev User', email: 'dev@trouve.ai', onboarded: true } : null);
  const [isAuthenticated, setIsAuthenticated] = useState(BYPASS_AUTH);
  const isOnboarded = Boolean(user?.onboarded ?? true);
  // Surface the onboarding scheduler automatically once we know the signed-in
  // user still needs to complete onboarding.
  useEffect(() => {
    if (isAuthenticated && !isOnboarded) {
      setOnboardingModalOpen(true);
    }
  }, [isAuthenticated, isOnboarded]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [, setIsUploadingFile] = useState(false);
  const websocketRef = useRef(null);
  const streamViewportRef = useRef(null);
  const pendingFileUploadRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const shouldReconnectRef = useRef(false);
  const allowPageLeaveRef = useRef(false);
  const agentModeRef = useRef(agentMode);

  useEffect(() => {
    agentModeRef.current = agentMode;
    storeAgentMode(agentMode);
  }, [agentMode]);

  const rejectPendingFileUpload = (message, clientUploadId = null) => {
    const pending = pendingFileUploadRef.current;
    if (!pending) return;
    if (clientUploadId && pending.clientUploadId !== clientUploadId) return;

    pendingFileUploadRef.current = null;
    setIsUploadingFile(false);
    setUploadedFiles((prev) => prev.map((file) => (
      file.clientUploadId === pending.clientUploadId
        ? { ...file, status: 'error' }
        : file
    )));
    pending.reject(new Error(message || 'File upload failed.'));
  };

  const resolvePendingFileUpload = (payload) => {
    const pending = pendingFileUploadRef.current;
    if (!pending) return;
    if (payload?.client_upload_id && pending.clientUploadId !== payload.client_upload_id) return;

    pendingFileUploadRef.current = null;
    setIsUploadingFile(false);
    pending.resolve(payload);
  };

  // Restore session on load (cookie-based)
  useEffect(() => {
    let cancelled = false;
    const restore = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/me`, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data && data.success) {
          setUser(data.user);
          setIsAuthenticated(true);
        }
      } catch (_) {
        // ignore
      }
    };
    restore();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setQuerySections([]);
      return;
    }

    let cancelled = false;
    const loadQuerySections = async () => {
      try {
        setQuerySections([]);
        const mode = encodeURIComponent(agentMode);
        const res = await fetch(getHttpURL(`knowledge/query-suggestions?mode=${mode}`), { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && data && data.success && Array.isArray(data.sections)) {
          setQuerySections(data.sections);
        }
      } catch (_) {}
    };

    loadQuerySections();
    return () => { cancelled = true; };
  }, [isAuthenticated, isOnboarded, agentMode]);

  // Load the current NetSuite session metadata (instance, user, role) once the
  // worker websocket is up; afterwards netsuite_session_change ws messages keep
  // it fresh.
  useEffect(() => {
    if (!isAuthenticated || !isOnboarded || !isConnected) return;

    let cancelled = false;
    const loadNetsuiteSession = async () => {
      try {
        const res = await fetch(getHttpURL('netsuite/session'), { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setNetsuiteSession(normalizeNetsuiteSession(data));
      } catch (_) {}
    };

    loadNetsuiteSession();
    return () => { cancelled = true; };
  }, [isAuthenticated, isOnboarded, isConnected]);

  // Authentication handlers
  const handleAuthSuccess = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleSignOut = () => {
    // Best-effort logout (clears cookie)
    try {
      fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/logout`, { method: 'POST', credentials: 'include' });
    } catch (_) {}
    setUser(null);
    setIsAuthenticated(false);
    setMessages([]);
    setBrowserFrameSrc(null);
    setBrowserStreamNotice(null);
    setContainerStatus(null);
    setNetsuiteSession(null);
    setAgentMode('netsuite');
    agentModeRef.current = 'netsuite';
    rejectPendingFileUpload('File upload cancelled.');
    setUploadedFiles([]);
    setIsUploadingFile(false);
    allowPageLeaveRef.current = true;
    shouldReconnectRef.current = false;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (websocketRef.current) {
      websocketRef.current.close();
    }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    allowPageLeaveRef.current = false;

    const isGuardState = (state) => (
      Boolean(state && typeof state === 'object' && state[SESSION_NAVIGATION_GUARD_KEY])
    );

    const pushNavigationGuard = () => {
      try {
        const currentState = window.history.state;
        if (isGuardState(currentState)) {
          return;
        }

        const baseState = currentState && typeof currentState === 'object' ? currentState : {};
        window.history.pushState(
          { ...baseState, [SESSION_NAVIGATION_GUARD_KEY]: true },
          '',
          window.location.href,
        );
      } catch (_) {
        // Some embedded browsers can reject history writes. beforeunload still covers navigation away.
      }
    };

    const shouldWarnBeforeLeaving = () => (
      isAuthenticated && !allowPageLeaveRef.current
    );

    const stopReconnectsForPageLeave = () => {
      shouldReconnectRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    };

    const handleBeforeUnload = (event) => {
      if (!shouldWarnBeforeLeaving()) return undefined;

      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    const handlePopState = () => {
      if (!shouldWarnBeforeLeaving()) return;

      const confirmed = window.confirm(SESSION_LEAVE_WARNING);
      if (confirmed) {
        allowPageLeaveRef.current = true;
        stopReconnectsForPageLeave();
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.setTimeout(() => {
          window.history.back();
        }, 0);
        return;
      }

      pushNavigationGuard();
    };

    pushNavigationGuard();
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isAuthenticated]);

  // Initialize WebSocket connection only when authenticated
  useEffect(() => {
    if (!isAuthenticated) return;
    if (!isOnboarded) {
      shouldReconnectRef.current = false;
      setIsConnected(false);
      setIsProcessing(false);
      setContainerStatus(null);
      setBrowserStreamNotice('Complete onboarding before starting a worker session.');
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (websocketRef.current) {
        try {
          websocketRef.current.close();
        } catch (_) {}
        websocketRef.current = null;
      }
      return;
    }

    shouldReconnectRef.current = true;

    const scheduleReconnect = () => {
      if (!shouldReconnectRef.current) return;
      const attempt = reconnectAttemptsRef.current;
      const baseDelayMs = Math.min(1000 * Math.pow(2, attempt), 30000);
      const jitterMs = Math.floor(Math.random() * 500);
      const delayMs = baseDelayMs + jitterMs;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      reconnectTimerRef.current = setTimeout(() => {
        reconnectAttemptsRef.current += 1;
        connectWebSocket();
      }, delayMs);
      console.log(`Scheduling WebSocket reconnect in ${delayMs}ms (attempt #${attempt + 1})`);
    };

    const connectWebSocket = () => {
      // Avoid creating multiple connections
      if (websocketRef.current && (websocketRef.current.readyState === WebSocket.OPEN || websocketRef.current.readyState === WebSocket.CONNECTING)) {
        return;
      }

      const ws = new WebSocket(getWebSocketURL('prompt_execute2_ws'));
      
      ws.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        reconnectAttemptsRef.current = 0;
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current);
          reconnectTimerRef.current = null;
        }

        if (streamViewportRef.current) {
          ws.send(JSON.stringify({
            type: 'browser_stream_viewport',
            ...streamViewportRef.current,
          }));
        }

        // Always reconcile the shared browser with the workspace selected in
        // this UI. This also repairs drift after a reconnect or browser restart.
        ws.send(JSON.stringify({ type: 'mode_switch', mode: agentModeRef.current }));
        
        // Send a ping to test the connection
        setTimeout(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'ping' }));
          }
        }, 1000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          // console.log('Received WebSocket message:', data); // Reduced logging

          // Update page metadata (best-effort; skip re-renders if unchanged)
          if (data && typeof data.current_page_url === 'string') {
            const nextUrl = data.current_page_url;
            setCurrentPageUrl((prev) => (prev === nextUrl ? prev : nextUrl));
          }
          
          // Live browser stream frames (CDP screencast)
          if (data && data.type === 'browser_frame' && data.data) {
            const fmt = data.format || 'jpeg';
            setBrowserFrameSrc(`data:image/${fmt};base64,${data.data}`);
            setBrowserStreamNotice(null);
            // If we receive a frame, the container is definitely up
            setContainerStatus('ready');
            return;
          }

          if (data && data.type === 'onboarding_required') {
            shouldReconnectRef.current = false;
            setIsConnected(false);
            setIsProcessing(false);
            setContainerStatus(null);
            setBrowserStreamNotice(data.message || 'Complete onboarding before starting a worker session.');
            try {
              ws.close();
            } catch (_) {}
            return;
          }

          // Container status updates
          if (data && data.type === 'container_status') {
            setContainerStatus(data.status);
            return;
          }

          // NetSuite instance/account id updates.
          if (data && data.type === 'instance_change') {
            return;
          }

          // Live NetSuite session metadata (instance name, user, role).
          if (data && data.type === 'netsuite_session_change') {
            const next = normalizeNetsuiteSession(data);
            if (next) setNetsuiteSession(next);
            return;
          }

          if (data && data.type === 'mode_changed') {
            const nextMode = String(data.mode || '').toLowerCase();
            if (AGENT_MODES.has(nextMode)) {
              agentModeRef.current = nextMode;
              setAgentMode(nextMode);
              setBrowserStreamNotice(null);
            }
            return;
          }

          if (data && data.type === 'history_cleared') {
            setUploadedFiles([]);
            return;
          }

          if (data && data.type === 'request_status') {
            if (data.status === 'running') {
              setIsProcessing(true);
            } else {
              setIsProcessing(false);
            }
            if (data.message) {
              const statusResponse = {
                id: Date.now() + 1,
                type: 'ai',
                content: data.message
              };
              setMessages(prev => [...prev, statusResponse]);
            }
            return;
          }

          if (data && data.type === 'file_upload_ack' && data.file_path) {
            setUploadedFiles((prev) => {
              const uploadId = String(data.client_upload_id || '');
              let matched = false;
              const next = prev.map((file) => {
                if (uploadId && file.clientUploadId === uploadId) {
                  matched = true;
                  return {
                    ...file,
                    filename: String(data.filename || file.filename || ''),
                    filePath: String(data.file_path || ''),
                    mimeType: String(data.mime_type || file.mimeType || ''),
                    status: 'uploaded',
                  };
                }
                return file;
              });
              if (!matched) {
                next.push({
                  id: uploadId || Date.now(),
                  clientUploadId: uploadId,
                  filename: String(data.filename || 'file'),
                  filePath: String(data.file_path || ''),
                  mimeType: String(data.mime_type || ''),
                  status: 'uploaded',
                });
              }
              return next;
            });
            resolvePendingFileUpload(data);
            return;
          }

          if (data && data.type === 'file_upload_error') {
            rejectPendingFileUpload(
              data.message || 'Failed to upload file.',
              String(data.client_upload_id || ''),
            );
            return;
          }

          // File attachment messages (generated files, logs, etc.)
          if (data && data.type === 'file' && data.filename && (data.content || data.content === '')) {
            const fileMsg = {
              id: Date.now() + 1,
              type: 'file',
              content: {
                filename: String(data.filename || ''),
                mimeType: String(data.mimeType || 'text/plain'),
                encoding: String(data.encoding || 'utf-8'),
                text: String(data.content || ''),
                downloadOnly: Boolean(data.downloadOnly),
              }
            };
            setMessages(prev => [...prev, fileMsg]);
            return;
          }

          if (data.type === 'error') {
            // Handle error messages
            const errorResponse = { 
              id: Date.now() + 1, 
              type: 'ai', 
              content: data.message || 'An error occurred'
            };
            setMessages(prev => [...prev, errorResponse]);
            setIsProcessing(false);
          } else if (data.message) {
            // Handle regular messages (updates, status, etc.)
            if (data.message === 'Successfully completed all operations') {
              // Final completion message - don't add to chat, just stop processing
              setIsProcessing(false);
            } else if (data.message === 'pong') {
              // Ping response, ignore
              return;
            } else if (data.message.startsWith('Browser status:')) {
              // Status message, ignore
              return;
            } else if (STREAM_NOTICE_MESSAGES.has(data.message)) {
              // Show browser-state notices in the stream placeholder area instead of chat.
              setBrowserFrameSrc(null);
              setBrowserStreamNotice(data.message);
              return;
            } else if (data.message.includes('cancelled') || data.message.includes('Cancellation')) {
              // Cancellation messages - stop processing and add to chat
              setIsProcessing(false);
              const cancelResponse = { 
                id: Date.now() + 1, 
                type: 'ai', 
                content: data.message
              };
              setMessages(prev => [...prev, cancelResponse]);
            } else {
              // Regular update message - add to chat
              const updateResponse = { 
                id: Date.now() + 1, 
                type: 'ai', 
                content: data.message
              };
              setMessages(prev => [...prev, updateResponse]);
            }
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
        setIsProcessing(false);
        rejectPendingFileUpload('WebSocket disconnected during file upload.');
        if (shouldReconnectRef.current) {
          scheduleReconnect();
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
        setIsProcessing(false);
        rejectPendingFileUpload('WebSocket error during file upload.');
        if (shouldReconnectRef.current) {
          scheduleReconnect();
        }
      };

      websocketRef.current = ws;
    };

    connectWebSocket();

    // Cleanup on unmount
    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (websocketRef.current) {
        try {
          websocketRef.current.close();
        } catch (_) {}
      }
    };
  }, [isAuthenticated, isOnboarded]);

  const handleStreamViewportChange = useCallback(({ width, height, force = false }) => {
    const viewport = {
      width: Math.max(0, Math.round(Number(width) || 0)),
      height: Math.max(0, Math.round(Number(height) || 0)),
    };
    if (!viewport.width || !viewport.height) return;
    streamViewportRef.current = viewport;

    const ws = websocketRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'browser_stream_viewport', ...viewport, force: Boolean(force) }));
    }
  }, []);

  const handleSendMessage = async (message, image = null) => {
    if (!isOnboarded) {
      setOnboardingModalOpen(true);
      return;
    }

    // Add user message immediately (with image and attached files for UI display)
    const readyFiles = uploadedFiles.filter((f) => f.status === 'uploaded');
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: message,
      image: image,
      files: readyFiles.length > 0 ? readyFiles : undefined,
    };
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    // Clear uploaded files from the composer now that they're part of the message
    setUploadedFiles([]);
    setIsUploadingFile(false);
    setBrowserStreamNotice(null);
    setIsProcessing(true);

    // Check if WebSocket is connected
    if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
      const errorResponse = { 
        id: Date.now() + 1, 
        type: 'ai', 
        content: 'WebSocket connection not available. Please refresh the page and try again.' 
      };
      setMessages([...updatedMessages, errorResponse]);
      setIsProcessing(false);
      return;
    }

    try {
      // If an image is attached, send it as a binary websocket frame (with a preceding JSON header).
      // The backend will associate the *latest* uploaded image with the next prompt_execute request.
      if (typeof image === 'string' && image.startsWith('data:image/')) {
        const match = image.match(/^data:(image\/[^;]+);base64,(.*)$/);
        if (match && match[1] && match[2]) {
          const mimeType = match[1];
          const b64 = match[2];
          try {
            // Notify backend an image upload is coming next.
            websocketRef.current.send(JSON.stringify({ type: 'image_upload', mime_type: mimeType }));
            // Decode base64 -> bytes and send as binary.
            const binary = atob(b64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) {
              bytes[i] = binary.charCodeAt(i);
            }
            websocketRef.current.send(bytes);
          } catch (e) {
            console.error('Error sending image binary over WebSocket:', e);
          }
        }
      }

      // Send message through WebSocket
      const websocketMessage = {
        type: 'prompt_execute',
        message,
        mode: agentModeRef.current,
      };
      
      websocketRef.current.send(JSON.stringify(websocketMessage));
    } catch (error) {
      console.error('Error sending WebSocket message:', error);
      const errorResponse = { 
        id: Date.now() + 1, 
        type: 'ai', 
        content: 'Sorry, I encountered an error sending your message. Please try again.' 
      };
      setMessages([...updatedMessages, errorResponse]);
      setIsProcessing(false);
    }
  };

  const handleCancelRequest = () => {
    // Ask the backend to cancel the running prompt task. The agent replies with
    // a "Cancellation requested..." message, which the ws handler above already
    // maps to isProcessing=false, so no optimistic state change here.
    try {
      if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
        websocketRef.current.send(JSON.stringify({ type: 'cancel' }));
      }
    } catch (_) {
      // ignore
    }
  };

  const handleModeChange = (nextMode) => {
    const normalized = String(nextMode || '').toLowerCase();
    if (!AGENT_MODES.has(normalized) || isProcessing) return;
    const isModeResync = normalized === agentModeRef.current;
    if (!isModeResync) {
      agentModeRef.current = normalized;
      storeAgentMode(normalized);
      setAgentMode(normalized);
      setMessages([]);
      setCurrentPageUrl(null);
      setUploadedFiles([]);
      setIsUploadingFile(false);
    }
    setBrowserStreamNotice(`Opening ${normalized === 'netsuite' ? 'NetSuite' : normalized === 'meridian' ? 'Meridian Portal' : 'GreenShield Portal'}…`);
    try {
      if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
        websocketRef.current.send(JSON.stringify({ type: 'mode_switch', mode: normalized }));
      }
    } catch (_) {}
  };

  const handleClearChat = () => {
    // Best-effort: clear server-side session history to keep backend and UI in sync.
    try {
      if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
        websocketRef.current.send(JSON.stringify({ type: 'clear' }));
      }
    } catch (_) {
      // ignore
    }
    setMessages([]);
    setIsProcessing(false);
    rejectPendingFileUpload('File upload cancelled.');
    setUploadedFiles([]);
    setIsUploadingFile(false);
  };

  const handleOpenLiveBrowser = async () => {
    if (isOpeningLiveBrowser) return;
    if (!isOnboarded) {
      setBrowserStreamNotice('Complete onboarding before opening the live browser.');
      return;
    }

    setIsOpeningLiveBrowser(true);
    let popup = null;
    try {
      // Open synchronously from the click handler so popup blockers allow it.
      // Do NOT use noopener here; some browsers return null and we can't navigate later.
      popup = window.open('about:blank', '_blank');
      if (popup) {
        try {
          popup.opener = null;
        } catch (_) {}
        popup.document.title = 'Opening Live Browser...';
        popup.document.body.innerHTML = '<p style="font-family:sans-serif;padding:16px;">Opening live browser...</p>';
      }

      const res = await fetch(getHttpURL('live-browser/link'), {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success || !data?.url) {
        throw new Error(data?.detail || data?.message || 'Unable to get live browser link');
      }

      if (popup && !popup.closed) {
        popup.location.replace(String(data.url));
      } else {
        // Fallback if browser blocked the initial popup handle.
        window.open(String(data.url), '_blank');
      }
    } catch (error) {
      console.error('Failed to open live browser:', error);
      if (popup && !popup.closed) {
        popup.document.title = 'Live Browser Error';
        popup.document.body.innerHTML =
          '<p style="font-family:sans-serif;padding:16px;color:#b91c1c;">Failed to open live browser.</p>';
      }
    } finally {
      setIsOpeningLiveBrowser(false);
    }
  };

  // Render Login component if not authenticated
  if (!isAuthenticated) {
    return <Login onAuthSuccess={handleAuthSuccess} />;
  }

  // Render the signed-in session workspace.
  return (
    <TrouveReplica
      messages={messages}
      isProcessing={isProcessing}
      isConnected={isConnected}
      user={user}
      onSignOut={handleSignOut}
      onClearChat={handleClearChat}
      onSendMessage={handleSendMessage}
      onCancelRequest={handleCancelRequest}
      netsuiteSession={netsuiteSession}
      agentMode={agentMode}
      onModeChange={handleModeChange}
      frameSrc={browserFrameSrc}
      streamNoticeMessage={browserStreamNotice}
      currentPageUrl={currentPageUrl}
      containerStatus={containerStatus}
      querySections={querySections}
      isOnboarded={isOnboarded}
      onboardingModalOpen={onboardingModalOpen}
      onCloseOnboardingModal={() => setOnboardingModalOpen(false)}
      onOpenLiveBrowser={handleOpenLiveBrowser}
      onRestartBrowser={isOnboarded ? () => handleSendMessage('restart browser') : null}
      isOpeningLiveBrowser={isOpeningLiveBrowser}
      onStreamViewportChange={handleStreamViewportChange}
    />
  );
}

export default App;
